<template>
  <div class="flex flex-col gap-6">
    <div v-if="!lockedClientId" class="max-w-sm">
      <UFormField label="Client" description="Code generation is client-specific." required>
        <UIClientpicker />
      </UFormField>
    </div>

    <div v-else-if="clientLabel" class="text-sm text-gray-500">
      Client: <span class="font-medium text-gray-900 dark:text-gray-100">{{ clientLabel }}</span>
    </div>

    <UAlert
      v-if="!effectiveClientId"
      color="warning"
      variant="soft"
      title="Select a client"
      description="Choose a client before generating point codes."
      icon="i-heroicons-information-circle-20-solid"
    />

    <div :class="{ 'opacity-60 pointer-events-none': !effectiveClientId }">
      <UAlert
        v-if="codesLoadError"
        color="error"
        variant="soft"
        :title="codesLoadError"
        icon="i-heroicons-exclamation-triangle-20-solid"
      />

      <UAlert
        v-else-if="codesLoaded && !pointshopCodes.length"
        color="warning"
        variant="soft"
        title="No campaigns available"
        description="This client has no pointshop codes configured."
        icon="i-heroicons-information-circle-20-solid"
      />

      <template v-else-if="pointshopCodes.length">
        <div class="flex flex-col gap-5">
          <div class="flex flex-col gap-5 max-w-sm">
            <UFormField label="Pointshop code" required>
              <USelectMenu
                v-model="selectedCodeId"
                :items="codeMenuItems"
                value-key="value"
                label-key="label"
                placeholder="Select a campaign"
                size="md"
                class="w-full"
                :loading="codesLoading"
              />
            </UFormField>

            <UFormField label="How many codes" required description="Between 1 and 100.">
              <UInput
                v-model.number="count"
                type="number"
                :min="1"
                :max="100"
                size="md"
                class="w-full"
              />
            </UFormField>
          </div>

          <UAlert
            v-if="generateError"
            color="error"
            variant="soft"
            :title="generateError"
            icon="i-heroicons-exclamation-triangle-20-solid"
          />

          <UButton
            color="primary"
            label="Generate"
            icon="i-heroicons-sparkles-20-solid"
            :loading="generating"
            :disabled="!canGenerate"
            @click="runGenerate"
          />

          <div
            v-if="generatedRows.length"
            class="flex flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
          >
            <div
              class="flex items-center justify-between gap-2 border-b border-gray-200 bg-card-secondary px-3 py-2 dark:border-gray-700 dark:bg-gray-900/50"
            >
              <span class="text-sm font-medium text-text-secondary dark:text-gray-300">Generated ({{ generatedRows.length }})</span>
              <UButton
                size="xs"
                color="neutral"
                variant="outline"
                label="Export to CSV"
                icon="i-heroicons-arrow-down-tray-20-solid"
                @click="exportGeneratedCsv"
              />
            </div>
            <div class="max-h-96 overflow-auto">
              <table class="w-full border-collapse text-left">
                <thead class="sticky top-0 z-[1] border-b border-gray-200 bg-card-secondary dark:border-gray-700 dark:bg-gray-900/95">
                  <tr>
                    <th
                      v-for="k in generatedHeaderKeys"
                      :key="k"
                      class="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-text-secondary"
                    >
                      {{ k }}
                    </th>
                    <th class="w-12 px-1 py-2 text-right text-xs font-semibold text-text-secondary" scope="col">
                      Copy
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in generatedRows"
                    :key="idx"
                    class="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td
                      v-for="k in generatedHeaderKeys"
                      :key="k"
                      class="max-w-0 select-text px-2 py-2.5 align-top font-sans text-base font-medium leading-relaxed tracking-wide break-all text-text-primary dark:text-gray-100"
                    >
                      {{ row[k] ?? '' }}
                    </td>
                    <td class="w-12 shrink-0 px-1 py-1 align-middle text-right">
                      <UButton
                        size="xs"
                        color="neutral"
                        variant="ghost"
                        :icon="
                          copiedRowIndex === idx
                            ? 'i-heroicons-check-20-solid'
                            : 'i-heroicons-clipboard-document-20-solid'
                        "
                        :aria-label="copiedRowIndex === idx ? 'Copied' : 'Copy code'"
                        class="shrink-0"
                        @click="copyGeneratedRow(row, idx)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <div v-else-if="codesLoading" class="flex items-center gap-2 text-sm text-text-secondary">
        <UIcon name="i-heroicons-arrow-path-20-solid" class="w-5 h-5 animate-spin" />
        Loading campaigns…
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GenerateCampaignCodesResponse, GetPointshopCodesResponse } from '~/types/api/client/pointshop'

interface Props {
  clientId?: number
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { selectedClient, accessLevel } = storeToRefs(userStore)
const toast = useToast()

const lockedClientId = computed(() => props.clientId ?? null)
const effectiveClientId = computed(() => lockedClientId.value ?? selectedClient.value?.id ?? null)

const clientLabel = computed(() => {
  if (!lockedClientId.value) return ''
  return accessLevel.value.find(client => client.client_id === lockedClientId.value)?.name ?? `Client ${lockedClientId.value}`
})

const codesLoading = ref(false)
const codesLoaded = ref(false)
const codesLoadError = ref('')
const pointshopCodes = ref<GetPointshopCodesResponse['pointshopCodes']>([])

const selectedCodeId = ref<number | undefined>(undefined)
const count = ref(1)

const generating = ref(false)
const generateError = ref('')
const generatedRows = ref<Record<string, string>[]>([])

const codeMenuItems = computed(() =>
  pointshopCodes.value.map((c) => ({
    label: c.name,
    value: c.id,
  }))
)

const generatedHeaderKeys = computed(() => {
  const first = generatedRows.value[0]
  return first ? Object.keys(first) : []
})

const copiedRowIndex = ref<number | null>(null)
let copiedRowReset: ReturnType<typeof setTimeout> | undefined

function copyTextForRow(row: Record<string, string>): string {
  const keys = generatedHeaderKeys.value.length ? generatedHeaderKeys.value : Object.keys(row)
  const codeKey = keys.find((k) => k.toLowerCase() === 'code')
  if (codeKey) return row[codeKey] ?? ''
  if (keys.length === 1) {
    const only = keys[0]
    return only ? (row[only] ?? '') : ''
  }
  return keys.map((k) => row[k] ?? '').join(',')
}

async function copyGeneratedRow(row: Record<string, string>, index: number) {
  const text = copyTextForRow(row)
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    if (copiedRowReset) clearTimeout(copiedRowReset)
    copiedRowIndex.value = index
    copiedRowReset = setTimeout(() => {
      copiedRowIndex.value = null
      copiedRowReset = undefined
    }, 2000)
  } catch {
    toast.add({
      title: 'Copy failed',
      description: 'Your browser may block clipboard access.',
      color: 'error',
      icon: 'i-heroicons-exclamation-triangle-20-solid',
    })
  }
}

const canGenerate = computed(() => {
  if (!selectedCodeId.value || !pointshopCodes.value.length) return false
  const n = Number(count.value)
  return Number.isFinite(n) && n >= 1 && n <= 100 && !generating.value
})

async function loadCodes() {
  if (!effectiveClientId.value) return
  codesLoading.value = true
  codesLoadError.value = ''
  codesLoaded.value = false
  try {
    const res = await $fetch<GetPointshopCodesResponse>('/api/client/webusers/pointshop/pointshop-codes', {
      query: { clientId: effectiveClientId.value },
      headers: useRequestHeaders(['cookie']) as HeadersInit,
    })
    pointshopCodes.value = res.pointshopCodes || []
    codesLoaded.value = true
    if (!res.success) {
      codesLoadError.value = res.message || 'Could not load pointshop codes.'
    }
    if (pointshopCodes.value.length) {
      selectedCodeId.value = pointshopCodes.value[0]?.id
    }
  } catch (e: unknown) {
    const err = e as { data?: { message?: string }; message?: string }
    codesLoadError.value = err.data?.message || err.message || 'Failed to load pointshop codes.'
    pointshopCodes.value = []
    codesLoaded.value = true
  } finally {
    codesLoading.value = false
  }
}

async function runGenerate() {
  if (!selectedCodeId.value) return
  generateError.value = ''
  generating.value = true
  try {
    const res = await $fetch<GenerateCampaignCodesResponse>('/api/client/webusers/pointshop/generate-campaign-codes', {
      method: 'POST',
      body: {
        campaignId: selectedCodeId.value,
        count: Math.min(100, Math.max(1, Math.floor(Number(count.value)) || 1)),
      },
      headers: useRequestHeaders(['cookie']) as HeadersInit,
    })
    generatedRows.value = res.rows || []
    if (!generatedRows.value.length) {
      toast.add({
        title: 'No codes in response',
        description: res.message || 'The server returned an empty or unparsable CSV.',
        color: 'warning',
      })
    } else {
      toast.add({
        title: 'Codes generated',
        description: `${generatedRows.value.length} row(s) returned.`,
        color: 'success',
      })
    }
  } catch (e: unknown) {
    const err = e as { data?: { message?: string }; message?: string }
    generateError.value = err.data?.message || err.message || 'Generation failed.'
  } finally {
    generating.value = false
  }
}

function escapeCsvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function recordsToCsv(rows: Record<string, string>[]): string {
  if (!rows.length) return ''
  const first = rows[0]
  if (!first) return ''
  const headers = Object.keys(first)
  const headerLine = headers.map(escapeCsvCell).join(',')
  const dataLines = rows.map((row) =>
    headers.map((h) => escapeCsvCell(row[h] ?? '')).join(',')
  )
  return [headerLine, ...dataLines].join('\r\n')
}

function exportGeneratedCsv() {
  const rows = generatedRows.value
  if (!rows.length) return
  const csv = recordsToCsv(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `pointshop-codes-client-${effectiveClientId.value}-${Date.now()}.csv`
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  toast.add({
    title: 'Exported',
    description: 'CSV file downloaded.',
    color: 'success',
    icon: 'i-heroicons-check-circle-20-solid',
  })
}

watch(effectiveClientId, (newId) => {
  if (newId) {
    generateError.value = ''
    generatedRows.value = []
    selectedCodeId.value = undefined
    count.value = 1
    loadCodes()
  }
}, { immediate: true })
</script>
